import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Stack, Drawer } from '@mui/material';
// hooks
import useAuth from '../../../hooks/useAuth';
import useResponsive from '../../../hooks/useResponsive';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';
// config
import { NAVBAR } from '../../../config';
import { homePathFor } from '../../../routes/paths';
// components
import Scrollbar from '../../../components/Scrollbar';
import { NavSectionVertical } from '../../../components/nav-section';
// portals
import { BRAND_LOGO_WHITE, BRAND_ICON_WHITE } from '../../auth/portals';
//
import navConfig from './NavConfig';
import NavbarAccount from './NavbarAccount';
import NavbarPortalCard from './NavbarPortalCard';
import CollapseButton from './CollapseButton';
import {
  portalForType,
  sidebarPaperSx,
  sidebarVars,
  sidebarBackground,
  SIDEBAR_FONT
} from './sidebarTokens';

// ----------------------------------------------------------------------
// The sidebar. One rail for all three portals, ALWAYS the Universe night —
// the brand navy at 12% lightness, lit by the brand's light blue (the owner,
// 07/10/2026: "keep it the same but colours more in line with the Universe
// brand identity") — whatever the app's light/dark mode. A dark rail against
// a light or dark workspace; the point is that it resembles the Universe
// backdrop, not the page beside it. See sidebarTokens.js for the palette.
//
// Expanded: the white Universe lock-up, the account card, the nav, and the
// star at the foot in the brand gradient. Collapsed (88px): the Universe globe takes the
// lock-up's place, the account card shrinks to its ringed avatar, and every
// item becomes a tile with its label in a tooltip. Hovering a collapsed rail
// expands it over the content until the pointer leaves.
// ----------------------------------------------------------------------

// The rail is its own world: always dark, never the app theme's light surface.
const SIDEBAR_MODE = 'dark';

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter
    })
  }
}));

// ----------------------------------------------------------------------

NavbarVertical.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }) {
  const { type } = useAuth();
  const portal = portalForType(type);
  // The mark at the top goes to THIS portal's dashboard, not to the root
  // portal chooser — inside a portal the logo is "home", not "leave".
  const home = homePathFor(type);

  const isDesktop = useResponsive('up', 'lg');

  const {
    isCollapse,
    collapseClick,
    collapseHover,
    onToggleCollapse,
    onHoverEnter,
    onHoverLeave
  } = useCollapseDrawer();

  const paperSx = sidebarPaperSx(portal, SIDEBAR_MODE);

  // On a phone the rail is a temporary drawer: it should close once a
  // destination has been picked, not sit over the page that was just opened.
  const { pathname } = useLocation();
  useEffect(() => {
    if (!isDesktop && isOpenSidebar) onCloseSidebar?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // `collapsed` is a parameter rather than the context value because the
  // collapse context derives its state from the same `viewNavbar` setting
  // the phone drawer uses for "open" — so on a phone it reads as collapsed
  // exactly when the drawer is open, which would hide every label behind
  // tooltips a touch screen cannot show. The phone drawer is always expanded.
  const renderContent = (collapsed) => (
    // The cosmos is painted on THIS Box, not (only) the Drawer paper. The
    // paper is a MuiPaper, and the theme's MuiPaper override forces
    // `backgroundImage: none` on every paper root — which strips the nebula —
    // while a stale cached bundle can leave the paper its default white
    // altogether. A plain Box is neither, so its opaque deep-space fill and
    // nebulae always show, over whatever the paper is. It also carries the
    // portal/mode custom properties, so every nav item has a var-bearing
    // ancestor.
    <Box
      sx={{
        height: 1,
        ...sidebarVars(portal, SIDEBAR_MODE),
        ...sidebarBackground(portal, SIDEBAR_MODE),
        color: 'var(--sb-text)',
        fontFamily: SIDEBAR_FONT
      }}
    >
      <Scrollbar
        sx={{
          height: 1,
          '& .simplebar-content': {
            height: 1,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Stack
          spacing={2.5}
          sx={{
            pt: 2.75,
            pb: 2,
            px: 2,
            flexShrink: 0,
            ...(collapsed && { px: 1.5, alignItems: 'stretch' })
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={collapsed ? 'center' : 'space-between'}
            sx={{ minHeight: 36, px: collapsed ? 0 : 0.5 }}
          >
            {collapsed ? (
              // The Universe globe icon stands in for the lock-up when there is
              // no room for a wordmark — the mark the whole product is known by,
              // white on the night rail and colour on the daylight one.
              <Box
                component={RouterLink}
                to={home}
                aria-label="LingoYou Universe — dashboard"
                sx={{
                  display: 'inline-flex',
                  lineHeight: 0,
                  transition: 'transform .2s ease',
                  '&:hover': { transform: 'scale(1.06)' }
                }}
              >
                <Box
                  component="img"
                  src={BRAND_ICON_WHITE}
                  alt="LingoYou Universe"
                  sx={{ width: 34, height: 34, objectFit: 'contain' }}
                />
              </Box>
            ) : (
              <Box
                component={RouterLink}
                to={home}
                aria-label="LingoYou Universe — dashboard"
                sx={{
                  display: 'inline-flex',
                  lineHeight: 0,
                  borderRadius: 1,
                  transition: 'opacity .15s ease',
                  '&:hover': { opacity: 0.85 }
                }}
              >
                {/* White lock-up: the rail is always the night sky. */}
                <Box
                  component="img"
                  src={BRAND_LOGO_WHITE}
                  alt="LingoYou Universe"
                  sx={{
                    height: 30,
                    width: 'auto',
                    maxWidth: 190,
                    display: 'block'
                  }}
                />
              </Box>
            )}

            {isDesktop && !collapsed && (
              <CollapseButton
                onToggleCollapse={onToggleCollapse}
                collapseClick={collapseClick}
              />
            )}
          </Stack>

          <NavbarAccount isCollapse={collapsed} />
        </Stack>

        <NavSectionVertical navConfig={navConfig} isCollapse={collapsed} />

        <Box sx={{ flexGrow: 1 }} />

        {!collapsed && <NavbarPortalCard portal={portal} />}
      </Scrollbar>
    </Box>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse
            ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH
            : NAVBAR.DASHBOARD_WIDTH
        },
        ...(collapseClick && {
          position: 'absolute'
        })
      }}
    >
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH, ...paperSx } }}
        >
          {renderContent(false)}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              ...paperSx,
              transition: (theme) =>
                theme.transitions.create(['width', 'box-shadow'], {
                  duration: theme.transitions.duration.standard
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH
              }),
              ...(collapseHover && {
                backgroundColor: 'var(--sb-ground-raised)',
                boxShadow: 'var(--sb-lift-shadow)'
              })
            }
          }}
        >
          {renderContent(isCollapse)}
        </Drawer>
      )}
    </RootStyle>
  );
}
